import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    FiDownload,
    FiFileText,
    FiCalendar,
    FiRefreshCw,
    FiCheckCircle,
    FiClock,
    FiAlertCircle,
} from "react-icons/fi";

// Format number in Indian Rupees
const formatINR = (amount) => {
    const numAmount = typeof amount === "number" ? amount : parseFloat(amount) || 0;
    return `₹${Number(numAmount.toFixed(0)).toLocaleString("en-IN")}`;
};

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export default function AccountStatement() {
    const [startDate, setStartDate] = useState("2023-01-01");
    const [endDate, setEndDate] = useState("2024-01-15");
    const [isGenerating, setIsGenerating] = useState(false);
    const [statementReady, setStatementReady] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [expiryTime, setExpiryTime] = useState(null);
    const [generatedStatements, setGeneratedStatements] = useState([]);

    // Static Data - Generated Statements History
    const statementHistory = [
        {
            id: 1,
            startDate: "2023-01-01",
            endDate: "2023-12-31",
            generatedDate: "2024-01-10",
            status: "Ready",
            downloadUrl: "#",
            expiryDate: "2024-01-17",
            fileSize: "2.5 MB",
        },
        {
            id: 2,
            startDate: "2023-06-01",
            endDate: "2023-12-31",
            generatedDate: "2024-01-05",
            status: "Expired",
            downloadUrl: null,
            expiryDate: "2024-01-12",
            fileSize: "1.8 MB",
        },
        {
            id: 3,
            startDate: "2022-01-01",
            endDate: "2022-12-31",
            generatedDate: "2023-12-20",
            status: "Ready",
            downloadUrl: "#",
            expiryDate: "2023-12-27",
            fileSize: "3.2 MB",
        },
    ];

    const handleGenerate = () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end dates");
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            alert("Start date cannot be after end date");
            return;
        }

        setIsGenerating(true);
        setStatementReady(false);
        setDownloadUrl(null);
        setExpiryTime(null);

        // Simulate async generation for large data
        setTimeout(() => {
            setIsGenerating(false);
            setStatementReady(true);

            // Simulate signed URL generation
            const expiry = new Date();
            expiry.setHours(expiry.getHours() + 24); // 24 hours expiry
            setExpiryTime(expiry);
            setDownloadUrl("https://example.com/statements/statement-20240115.pdf");

            // Add to history
            const newStatement = {
                id: generatedStatements.length + 1,
                startDate: startDate,
                endDate: endDate,
                generatedDate: new Date().toISOString().split("T")[0],
                status: "Ready",
                downloadUrl: "https://example.com/statements/statement-20240115.pdf",
                expiryDate: expiry.toISOString().split("T")[0],
                fileSize: "2.1 MB",
            };
            setGeneratedStatements([newStatement, ...generatedStatements]);

            alert("Statement generated successfully! Download link is ready.");
        }, 3000);
    };

    const handleDownload = (url, statementId) => {
        if (!url) {
            alert("Download link has expired. Please generate a new statement.");
            return;
        }
        // In real implementation, this would download the PDF
        alert(`Downloading statement ${statementId}...`);
        window.open(url, "_blank");
    };

    const checkExpiry = (expiryDate) => {
        return new Date(expiryDate) > new Date();
    };

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f1f5f9" }}>
                    {/* Header */}
                    <div className="card shadow-sm p-4 mb-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h3 className="text-primary mb-2">Account Statement</h3>
                                <nav
                                    aria-label="breadcrumb"
                                    style={{ "--bs-breadcrumb-divider": "none" }}
                                >
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <NavLink to="/overview">Dashboard</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item">
                                            <NavLink to="/reports/account-statement">Reports</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item active">Account Statement</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>

                    {/* Generate Statement Section */}
                    <div className="card shadow-sm p-4 mb-4">
                        <h5 className="text-primary mb-3">
                            {/* <FiFileText className="me-2" /> */}
                            Generate Consolidated Statement
                        </h5>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-semibold">
                                    {/* <FiCalendar className="me-1" /> */}
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={endDate}
                                />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-semibold">
                                    {/* <FiCalendar className="me-1" /> */}
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                />
                            </div>
                            <div className="col-md-4 mb-3 d-flex align-items-end">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            {/* <FiRefreshCw className="me-1" /> */}
                                            Generate Statement
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Statement Ready Notification */}
                        {statementReady && downloadUrl && (
                            <div className="alert alert-success mt-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                    <div className="d-flex align-items-center">
                                        <FiCheckCircle className="me-2" style={{ fontSize: "20px" }} />
                                        <div>
                                            <strong>Statement Ready!</strong>
                                            <div className="small">
                                                Your consolidated statement for the period{" "}
                                                {formatDate(startDate)} to {formatDate(endDate)} is
                                                ready for download.
                                            </div>
                                            {expiryTime && (
                                                <div className="small text-muted mt-1">
                                                    <FiClock className="me-1" />
                                                    Download link expires on:{" "}
                                                    {formatDate(expiryTime.toISOString())} at{" "}
                                                    {expiryTime.toLocaleTimeString("en-IN", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-success mt-2 mt-md-0"
                                        onClick={() => handleDownload(downloadUrl, "current")}
                                    >
                                        <FiDownload className="me-1" />
                                        Download PDF
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Info Alert */}
                        <div className="alert alert-info mt-3 mb-0">
                            <div className="d-flex align-items-start">
                                <FiAlertCircle className="me-2 mt-1" />
                                <div>
                                    <strong>Important Information:</strong>
                                    <ul className="mb-0 mt-2">
                                        <li>
                                            Large date ranges may take time to process (async
                                            generation).
                                        </li>
                                        <li>
                                            Download links are signed URLs with 24-hour expiry for
                                            security.
                                        </li>
                                        <li>
                                            Please download your statement before the expiry time.
                                        </li>
                                        <li>
                                            The consolidated statement includes all transactions,
                                            holdings, and portfolio summary for the selected period.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Statement Summary Cards */}
                    <div className="row mb-4">
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">Total Statements</h6>
                                    <h4 className="text-primary mb-0">
                                        {statementHistory.length + generatedStatements.length}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">Available Downloads</h6>
                                    <h4 className="text-success mb-0">
                                        {[
                                            ...statementHistory,
                                            ...generatedStatements,
                                        ].filter((s) => s.status === "Ready" && checkExpiry(s.expiryDate)).length}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">Expired Links</h6>
                                    <h4 className="text-warning mb-0">
                                        {[
                                            ...statementHistory,
                                            ...generatedStatements,
                                        ].filter((s) => !checkExpiry(s.expiryDate)).length}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Statement History */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Generated Statements</h5>
                            <span className="badge bg-primary">
                                {statementHistory.length + generatedStatements.length} Total
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Period</th>
                                            <th>Generated Date</th>
                                            <th>File Size</th>
                                            <th>Status</th>
                                            <th>Expiry Date</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...generatedStatements, ...statementHistory].map(
                                            (statement) => {
                                                const isExpired = !checkExpiry(statement.expiryDate);
                                                const isReady =
                                                    statement.status === "Ready" && !isExpired;

                                                return (
                                                    <tr key={statement.id}>
                                                        <td>
                                                            <div>
                                                                <strong>
                                                                    {formatDate(statement.startDate)} -{" "}
                                                                    {formatDate(statement.endDate)}
                                                                </strong>
                                                            </div>
                                                        </td>
                                                        <td>{formatDate(statement.generatedDate)}</td>
                                                        <td>{statement.fileSize}</td>
                                                        <td>
                                                            {isReady ? (
                                                                <span className="badge bg-success">
                                                                    Ready
                                                                </span>
                                                            ) : isExpired ? (
                                                                <span className="badge bg-danger">
                                                                    Expired
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-warning">
                                                                    {statement.status}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {statement.expiryDate
                                                                ? formatDate(statement.expiryDate)
                                                                : "N/A"}
                                                        </td>
                                                        <td className="text-center">
                                                            {isReady ? (
                                                                <button
                                                                    className="btn btn-primary"
                                                                    onClick={() =>
                                                                        handleDownload(
                                                                            statement.downloadUrl,
                                                                            statement.id
                                                                        )
                                                                    }
                                                                >
                                                                    <FiDownload className="me-1" />
                                                                    Download
                                                                </button>
                                                            ) : isExpired ? (
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    disabled
                                                                    title="Link expired. Generate a new statement."
                                                                >
                                                                    <FiAlertCircle className="me-1" />
                                                                    Expired
                                                                </button>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    Not Available
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* What's Included Section */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">What's Included in the Statement</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-primary mb-2">
                                        <FiFileText className="me-1" />
                                        Transaction Details
                                    </h6>
                                    <ul className="list-unstyled ms-3">
                                        <li>• All purchases (Lumpsum & SIP)</li>
                                        <li>• Redemptions and switches</li>
                                        <li>• Dividend payouts</li>
                                        <li>• Transaction dates and amounts</li>
                                    </ul>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-primary mb-2">
                                        <FiFileText className="me-1" />
                                        Portfolio Summary
                                    </h6>
                                    <ul className="list-unstyled ms-3">
                                        <li>• Current holdings</li>
                                        <li>• Fund-wise allocation</li>
                                        <li>• Category and AMC distribution</li>
                                        <li>• Risk profile breakdown</li>
                                    </ul>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-primary mb-2">
                                        <FiFileText className="me-1" />
                                        Performance Metrics
                                    </h6>
                                    <ul className="list-unstyled ms-3">
                                        <li>• Total invested amount</li>
                                        <li>• Current portfolio value</li>
                                        <li>• Gains/Losses (Realized & Unrealized)</li>
                                        <li>• XIRR calculations</li>
                                    </ul>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h6 className="text-primary mb-2">
                                        <FiFileText className="me-1" />
                                        Tax Information
                                    </h6>
                                    <ul className="list-unstyled ms-3">
                                        <li>• Capital gains summary</li>
                                        <li>• STCG/LTCG breakdown</li>
                                        <li>• Tax implications</li>
                                        <li>• Folio-wise details</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
           
        </>
    );
}
